import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * Keeps a Solenex support session out of the pages it should not open.
 *
 * Two kinds of page carry this. Some load Planning Center data with the
 * signed-in user's own OAuth token, which a support agent does not have —
 * assertPcoOrganization now refuses those cleanly rather than revoking the
 * agent's session, but a page that cannot load is still a dead end. The rest
 * send messages or change billing: real, irreversible actions on a customer's
 * account.
 *
 * This is not a security boundary and does not pretend to be one. A support
 * session genuinely holds the customer's owner claims — that is the whole
 * design, and it is what lets every existing Firestore rule and callable work
 * unchanged. What actually holds a support agent to account is the audit log,
 * which records the session, its stated reason, and every high-risk write made
 * inside it. This guard is here so those writes are deliberate rather than
 * accidental.
 *
 * Reads the claim rather than PlatformAdminService, so it does not depend on
 * loadState() having run before the first navigation resolves.
 *
 * Usage: canActivate: [AuthGuard, OrganizationGuard, supportModeGuard()]
 */
export const supportModeGuard = (): CanActivateFn => async () => {
    const auth = inject(Auth);
    const router = inject(Router);

    try {
        const user = await firstValueFrom(authState(auth).pipe(take(1)));
        if (!user) return true; // AuthGuard owns this case.

        const token = await user.getIdTokenResult();

        if (token.claims['impersonating'] === true) {
            return router.createUrlTree(['/dashboard']);
        }

        return true;
    } catch (error) {
        // Fail open, like every other customer-facing guard in the app: a
        // transient token read must not lock a paying customer out of their own
        // subscription page. The consequence of being wrong here is a support
        // agent seeing a page they should not have, not a customer losing
        // access to one they own.
        console.error('Support mode guard error:', error);
        return true;
    }
};
